import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getScan, createScan, updateScan, GetScanResponse } from '../../api/scans';
import { toast } from 'react-toastify';
import { RootState } from '../../store/store';
import Loader from '../BaseComponents/Loader';
import Button from '../BaseComponents/Button';
import Modal from '../BaseComponents/Modal';
import { getStatusDisplayForItem, getSystemStatusDisplay } from './fbsFindingStatuses';
import {
  getDetailStatusPillClass,
  getFindingFilters,
  sortSystemEntries,
  FbsReportData,
  FbsReportItem,
  FbsReportSystem,
  formatScanDateTimeLine,
  pickStringAcrossScanSources,
  pickSystemNoteFromSystem,
  resolveUsesFbsFindingStatusV2
} from './fullBodyScanReportUtils';
import FullBodyScanConsolidated from './FullBodyScanConsolidated';
import { triggerConsolidatedReportDownload } from './triggerConsolidatedReportDownload';
import './FullBodyScan.scss';

interface ReportItem {
  id?: number;
  condition?: string;
  status?: string;
  description?: string | null;
  detailedNote?: string | null;
}

interface ReportOrgan {
  name?: string;
  organDescription?: string | null;
  organStatus?: string;
  items?: ReportItem[];
}

interface ReportSystem {
  name?: string;
  status?: string;
  variant?: string;
  systemNote?: string;
  system_note?: string;
  organs?: Record<string, ReportOrgan>;
}

type ReportData = Record<string, ReportSystem>;

const FullBodyScan: React.FC = () => {
  const location = useLocation();
  const memberId = location.pathname.split('/')[2];
  const memberData = useSelector((state: RootState) => state.member.memberData);
  const [scanData, setScanData] = useState<GetScanResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [bookingNumber, setBookingNumber] = useState<string>('');
  const [showInApp, setShowInApp] = useState<boolean>(true);
  const [skipDob, setSkipDob] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [reportView, setReportView] = useState<'detail' | 'consolidated' | 'notes'>('detail');
  const [detailSystemTab, setDetailSystemTab] = useState<string | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [showIncompleteReport, setShowIncompleteReport] = useState<boolean>(false);
  const [consolidatedFilterStatuses, setConsolidatedFilterStatuses] = useState<string[]>([]);

  const handleDownloadConsolidatedReport = async () => {
    if (!scanData) return;
    const bookingId = scanData?.booking_number || scanData?.booking_id || '';
    setIsDownloadingReport(true);
    try {
      await triggerConsolidatedReportDownload({
        scanData,
        reportData: reportData as FbsReportData,
        member: memberData,
        bookingId,
        selectedStatuses: consolidatedFilterStatuses
      });
    } catch (error) {
      console.error('Failed to download consolidated report', error);
    } finally {
      setIsDownloadingReport(false);
    }
  };

  useEffect(() => {
    setShowIncompleteReport(false);
    setConsolidatedFilterStatuses([]);
  }, [memberId]);

  useEffect(() => {
    const fetchScan = async () => {
      if (!memberId) return;
      
      try {
        setIsLoading(true);
        const data = await getScan(memberId);
        setScanData(data);
        if (data?.show_in_app !== undefined) {
          setShowInApp(data.show_in_app);
        }
        if (data?.skip_dob !== undefined) {
          setSkipDob(data.skip_dob);
        } else {
          setSkipDob(false);
        }
        if (!data) {
          setIsFormOpen(true);
        }
      } catch (error: any) {
        const errorMessage = error?.detail || error?.message || 'Failed to fetch scan data';
        toast.error(errorMessage);
        setIsFormOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScan();
  }, [memberId]);

  const handleSubmit = async (isUpdate: boolean = false) => {
    if (!bookingNumber.trim()) {
      toast.error('Please enter a booking number');
      return;
    }

    if (!memberId) {
      toast.error('Member ID not found');
      return;
    }

    try {
      if (isUpdate) {
        setIsUpdating(true);
      } else {
        setIsSubmitting(true);
      }

      const request = {
        member_id: memberId,
        booking_id: bookingNumber.trim(),
        scan_type: 'full_body_mri' as const,
        show_in_app: showInApp,
        skip_dob: skipDob
      };

      if (isUpdate) {
        await updateScan(request);
        toast.success('Scan information updated successfully');
      } else {
        await createScan(request);
        toast.success('Scan created successfully');
      }

      // Refresh scan data
      const data = await getScan(memberId);
      setScanData(data);
      if (data?.show_in_app !== undefined) {
        setShowInApp(data.show_in_app);
      }
      if (data?.skip_dob !== undefined) {
        setSkipDob(data.skip_dob);
      } else {
        setSkipDob(false);
      }
      setIsFormOpen(false);
      setBookingNumber('');
    } catch (error: any) {
      const errorMessage = error?.detail || error?.message || `Failed to ${isUpdate ? 'update' : 'create'} scan`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setIsUpdating(false);
    }
  };

  const handleUpdateClick = () => {
    if (scanData?.booking_id) {
      setBookingNumber(scanData.booking_id);
    }
    if (scanData?.show_in_app !== undefined) {
      setShowInApp(scanData.show_in_app);
    }
    if (scanData?.skip_dob !== undefined) {
      setSkipDob(scanData.skip_dob);
    } else {
      setSkipDob(false);
    }
    setIsFormOpen(true);
  };

  const handleShowInAppToggle = async (newValue: boolean) => {
    if (!scanData || !memberId) return;

    try {
      const request = {
        member_id: memberId,
        booking_id: scanData.booking_id || '',
        scan_type: 'full_body_mri' as const,
        show_in_app: newValue,
        skip_dob: skipDob
      };

      await updateScan(request);
      setShowInApp(newValue);
      
      // Refresh scan data
      const data = await getScan(memberId);
      setScanData(data);
      if (data?.skip_dob !== undefined) {
        setSkipDob(data.skip_dob);
      } else {
        setSkipDob(false);
      }
      toast.success('Show in App setting updated successfully');
    } catch (error: any) {
      const errorMessage = error?.detail || error?.message || 'Failed to update show in app setting';
      toast.error(errorMessage);
      // Revert the toggle on error
      setShowInApp(scanData.show_in_app || false);
    }
  };

  const reportData = useMemo((): ReportData => (scanData?.report_data || {}) as ReportData, [scanData?.report_data]);
  const usesFbsFindingStatusV2 = useMemo(
    () =>
      resolveUsesFbsFindingStatusV2(
        (scanData || {}) as Record<string, unknown>,
        reportData as FbsReportData
      ),
    [scanData, reportData]
  );
  const findingFilters = useMemo(
    () => getFindingFilters(usesFbsFindingStatusV2),
    [usesFbsFindingStatusV2]
  );

  useEffect(() => {
    setConsolidatedFilterStatuses(findingFilters.map((f) => f.id));
  }, [findingFilters, scanData?.booking_id, scanData?.booking_number]);
  const systemEntries = Object.entries(reportData);
  const sortedSystemEntries = useMemo(
    () => sortSystemEntries(Object.entries(reportData) as [string, FbsReportSystem][]),
    [reportData]
  );

  useEffect(() => {
    if (sortedSystemEntries.length === 0) {
      setDetailSystemTab(null);
      return;
    }
    setDetailSystemTab((prev) => {
      if (prev && sortedSystemEntries.some(([k]) => k === prev)) return prev;
      return sortedSystemEntries[0][0];
    });
  }, [sortedSystemEntries]);

  const scanPayload = (scanData || {}) as unknown as Record<string, unknown>;
  const reportingStatus =
    pickStringAcrossScanSources(scanPayload, ['reporting_status', 'reportingStatus']) || '';
  const scanStatusLabel = reportingStatus || scanData?.status || '';
  const isReportPublishedToMember =
    reportingStatus.toLowerCase() === 'publish_to_member';

  const hasGlobalSummary = !!scanData?.report_summary;
  const notesEntries = systemEntries.flatMap(([systemName, system]) =>
    Object.entries(system?.organs || {}).flatMap(([organName, organ]) =>
      (organ?.items || [])
        .filter((item) => item?.detailedNote)
        .map((item) => ({
          id: item.id || `${systemName}-${organName}-${item.condition}`,
          systemName: system?.name || systemName,
          organName: organ?.name || organName,
          condition: item.condition || '-',
          note: item.detailedNote || ''
        }))
    )
  );

  const formatDateTime = (value?: string): string => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusPillClass = (status?: string): string =>
    getDetailStatusPillClass(status, usesFbsFindingStatusV2);

  const getItemStatusDisplay = (item: FbsReportItem) =>
    getStatusDisplayForItem(item, usesFbsFindingStatusV2);

  const getSystemStatusLabel = (status?: string) =>
    getSystemStatusDisplay({ status }, usesFbsFindingStatusV2).label;

  if (isLoading) {
    return (
      <div className="full-body-scan-container">
        <Loader />
      </div>
    );
  }

  const hasScanData = !!scanData;
  const hasReportData = systemEntries.length > 0;
  const showReport = isReportPublishedToMember || showIncompleteReport;

  return (
    <div className="full-body-scan-container">
      {hasScanData ? (
        <>
          <div className="full-body-scan-header sideBarPad2">
            <div className="header-left">
              <h2>Full Body Scan</h2>
              {scanData && (
                <div className="show-in-app-toggle-header">
                  <label htmlFor="show-in-app-header" className="toggle-label-text">
                    Show in App
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="show-in-app-header"
                      checked={showInApp}
                      onChange={(e) => handleShowInAppToggle(e.target.checked)}
                      className="toggle-input"
                    />
                    <label htmlFor="show-in-app-header" className="toggle-label">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="full-body-scan-header-actions">
              {hasReportData && isReportPublishedToMember ? (
                <Button
                  text="Download report"
                  onClick={handleDownloadConsolidatedReport}
                  isSecondary={true}
                  variant="download"
                  isLoading={isDownloadingReport}
                  isDisabled={isDownloadingReport}
                />
              ) : null}
              <Button
                text="Update scan information"
                onClick={handleUpdateClick}
                isSecondary={true}
              />
            </div>
          </div>
          <div className="full-body-scan-content">
            <div className="scan-summary-grid">
              <div className="summary-card">
                <span className="summary-label">Booking ID</span>
                <span className="summary-value">{scanData?.booking_number || scanData?.booking_id || '-'}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Scan date &amp; time</span>
                <span className="summary-value">
                  {scanData
                    ? formatScanDateTimeLine(scanData as unknown as Record<string, unknown>)
                    : '-'}
                </span>
              </div>
              <div className="summary-card">
                <span className="summary-label">GP</span>
                <span className="summary-value">{scanData?.gp_name || '-'}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Radiologist</span>
                <span className="summary-value">{scanData?.radiologist_name || '-'}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Status</span>
                <span className={`status-pill ${getStatusPillClass(scanStatusLabel)}`}>
                  {scanStatusLabel || '-'}
                </span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Updated</span>
                <span className="summary-value">{formatDateTime(scanData?.updated_at)}</span>
              </div>
            </div>

            {!showReport ? (
              <div className="full-body-scan-notice full-body-scan-notice--hero" role="status">
                <h3 className="full-body-scan-notice__title">Report not published yet</h3>
                <p className="full-body-scan-notice__message">
                  Will be available once published for the member.
                </p>
                {hasReportData && (
                  <button
                    type="button"
                    className="full-body-scan-notice__link"
                    onClick={() => setShowIncompleteReport(true)}
                  >
                    View incomplete report
                  </button>
                )}
              </div>
            ) : hasReportData ? (
              <>
                {!isReportPublishedToMember && (
                  <div className="full-body-scan-draft-banner" role="status">
                    <span>Viewing unpublished report — not visible to the member.</span>
                    <button
                      type="button"
                      className="full-body-scan-draft-banner__link"
                      onClick={() => setShowIncompleteReport(false)}
                    >
                      Hide report
                    </button>
                  </div>
                )}
                <div className="report-view-toggle">
                  <button
                    className={`view-btn ${reportView === 'detail' ? 'active' : ''}`}
                    onClick={() => setReportView('detail')}
                    type="button"
                  >
                    Detail Report
                  </button>
                  <button
                    className={`view-btn ${reportView === 'consolidated' ? 'active' : ''}`}
                    onClick={() => setReportView('consolidated')}
                    type="button"
                  >
                    Consolidated Report
                  </button>
                  <button
                    className={`view-btn ${reportView === 'notes' ? 'active' : ''}`}
                    onClick={() => setReportView('notes')}
                    type="button"
                  >
                    Notes
                  </button>
                </div>

                {scanData ? (
                  <div
                    className={`fbs-consolidated-print-host ${
                      reportView === 'consolidated' ? 'is-visible' : 'is-screen-hidden'
                    }`}
                    aria-hidden={reportView !== 'consolidated'}
                  >
                    <FullBodyScanConsolidated
                      scanData={scanData}
                      reportData={reportData as FbsReportData}
                      member={memberData}
                      onDownload={
                        isReportPublishedToMember
                          ? handleDownloadConsolidatedReport
                          : undefined
                      }
                      isDownloading={isDownloadingReport}
                      selectedStatuses={consolidatedFilterStatuses}
                      onSelectedStatusesChange={setConsolidatedFilterStatuses}
                    />
                  </div>
                ) : null}

                {reportView === 'notes' ? (
                  <div className="notes-section">
                    {hasGlobalSummary && (
                      <div className="note-card">
                        <div className="note-meta">Report Summary</div>
                        <div className="note-content">{scanData?.report_summary}</div>
                      </div>
                    )}
                    {notesEntries.length > 0 ? (
                      notesEntries.map((entry) => (
                        <div key={entry.id} className="note-card">
                          <div className="note-meta">
                            {entry.systemName} / {entry.organName} / {entry.condition}
                          </div>
                          <div className="note-content">{entry.note}</div>
                        </div>
                      ))
                    ) : (
                      <div className="no-notes">No notes available for this report.</div>
                    )}
                  </div>
                ) : reportView === 'detail' ? (
                  <>
                    {sortedSystemEntries.length > 0 && (
                      <div className="body-system-tabs report-view-toggle" role="tablist" aria-label="Body systems">
                        {sortedSystemEntries.map(([key, sys]) => (
                          <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={detailSystemTab === key}
                            className={`view-btn body-system-tab ${detailSystemTab === key ? 'active' : ''}`}
                            onClick={() => setDetailSystemTab(key)}
                          >
                            <span className="body-system-tab-name">{sys.name || key}</span>
                            <span className="body-system-tab-status">
                              {getSystemStatusLabel(sys.status)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="system-sections">
                      {(() => {
                        const entry = sortedSystemEntries.find(([k]) => k === detailSystemTab);
                        if (!entry) {
                          return (
                            <div className="full-body-scan-empty">
                              <p>Select a body system to view findings.</p>
                            </div>
                          );
                        }
                        const [systemName, system] = entry;
                        return (
                          <div key={systemName} className="system-section">
                            <div className="system-section-header">
                              <h3>{system?.name || systemName}</h3>
                              <span className={`status-pill ${getStatusPillClass(system?.status)}`}>
                                {getSystemStatusLabel(system?.status)}
                              </span>
                            </div>
                            <div className="system-note-block">
                              <div className="system-note-label">System notes</div>
                              <p className="system-note">
                                {pickSystemNoteFromSystem(system as FbsReportSystem) || 'No notes available for this system.'}
                              </p>
                            </div>

                            {Object.entries(system?.organs || {}).map(([organName, organ]) => (
                              <div key={organName} className="organ-section">
                                <div className="organ-header">
                                  <h4>{organ?.name || organName}</h4>
                                  <span className={`status-pill ${getStatusPillClass(organ?.organStatus)}`}>
                                    {getSystemStatusLabel(organ?.organStatus)}
                                  </span>
                                </div>
                                <div className="findings-table">
                                  <div className="findings-row findings-head">
                                    <div>Condition</div>
                                    <div>Notes</div>
                                    <div>Status</div>
                                    <div>Description</div>
                                  </div>
                                  {(organ?.items || []).map((item) => (
                                    <div key={item.id || `${organName}-${item.condition}`} className="findings-row">
                                      <div>{item.condition || '-'}</div>
                                      <div className="findings-cell-notes">
                                        {item.detailedNote?.trim() ? item.detailedNote : '-'}
                                      </div>
                                      <div>
                                        {(() => {
                                          const statusDisplay = getItemStatusDisplay(item);
                                          return (
                                            <span
                                              className={`status-pill ${getStatusPillClass(statusDisplay.label)}`}
                                            >
                                              {statusDisplay.label}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                      <div>{item.description || '-'}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className="full-body-scan-empty">
                <p>Scan exists but report data is not available yet.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="full-body-scan-empty">
          <h2>Full Body Scan</h2>
          <p>No scan found for this member. Please enter a booking number to create a scan.</p>
          <Button
            text="Add Booking Number"
            onClick={() => {
              setShowInApp(true);
              setSkipDob(false);
              setIsFormOpen(true);
            }}
          />
        </div>
      )}

      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setBookingNumber('');
          if (scanData?.show_in_app !== undefined) {
            setShowInApp(scanData.show_in_app);
          } else {
            setShowInApp(true);
          }
          if (scanData?.skip_dob !== undefined) {
            setSkipDob(scanData.skip_dob);
          } else {
            setSkipDob(false);
          }
        }}
        title={scanData ? 'Update Scan Information' : 'Create Scan'}
        width="500px"
      >
        <div className="full-body-scan-form">
          <div className="form-group">
            <label htmlFor="booking-number">Booking Number</label>
            <input
              id="booking-number"
              type="text"
              className="form-input"
              value={bookingNumber}
              onChange={(e) => setBookingNumber(e.target.value)}
              placeholder="Enter booking number"
            />
          </div>
          <div className="form-group form-group--row">
            <label htmlFor="show-in-app">Show in App</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="show-in-app"
                checked={showInApp}
                onChange={(e) => setShowInApp(e.target.checked)}
                className="toggle-input"
              />
              <label htmlFor="show-in-app" className="toggle-label">
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="form-group form-group--row">
            <label htmlFor="skip-dob">Skip DOB check</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="skip-dob"
                checked={skipDob}
                onChange={(e) => setSkipDob(e.target.checked)}
                className="toggle-input"
              />
              <label htmlFor="skip-dob" className="toggle-label">
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="form-actions">
            <Button
              text={scanData ? 'Update' : 'Create'}
              onClick={() => handleSubmit(!!scanData)}
              isLoading={isSubmitting || isUpdating}
              isDisabled={!bookingNumber.trim() || isSubmitting || isUpdating}
            />
            <Button
              text="Cancel"
              onClick={() => {
                setIsFormOpen(false);
                setBookingNumber('');
                if (scanData?.show_in_app !== undefined) {
                  setShowInApp(scanData.show_in_app);
                } else {
                  setShowInApp(true);
                }
                if (scanData?.skip_dob !== undefined) {
                  setSkipDob(scanData.skip_dob);
                } else {
                  setSkipDob(false);
                }
              }}
              isSecondary={true}
              isDisabled={isSubmitting || isUpdating}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FullBodyScan;

